"use client";

import React from "react";
import { MapPin, Phone, Mail, ShieldCheck, Paperclip, Send, Loader, Building } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
function TipForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in duration-500">
        <div className="size-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
          <ShieldCheck className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-default font-semibold text-foreground">Solicitação Enviada</h3>
        <p className="text-muted-foreground max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-primary text-primary hover:bg-primary/10">
          Enviar Nova Mensagem
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <Badge variant="outline" className="bg-primary/5 text-primary border-primary/20 gap-1.5 py-1 px-3 font-default">
          <ShieldCheck className="size-3.5" />
          Conexão Criptografada
        </Badge>
      </div>

      <FormField control={control} name="anonymous" render={({
      field
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border border-border p-4 bg-muted/30">
            <FormControl>
              <input type="checkbox" className="mt-0.5 size-4 shrink-0 rounded-sm border-primary ring-offset-background focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 accent-primary" checked={field.value} onChange={field.onChange} />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="text-base font-default font-medium cursor-pointer">
                Tratamento Confidencial
              </FormLabel>
              <FormDescription className="font-default font-normal text-sm text-muted-foreground">
                Marque para priorizar discrição absoluta em nosso primeiro contato.
              </FormDescription>
            </div>
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default font-medium">Nome Completo</FormLabel>
              <FormControl>
                <Input placeholder="Ex: Roberto Silva" className="bg-background border-border focus:border-primary" {...field} value={field.value || ''} />
              </FormControl>
              <FormDescription className="font-default font-normal text-sm">Como devemos nos referir a você.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Por favor, insira um e-mail corporativo válido"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default font-medium">E-mail Corporativo</FormLabel>
              <FormControl>
                <Input type="email" placeholder="roberto@empresa.com.br" className="bg-background border-border focus:border-primary" {...field} value={field.value || ''} />
              </FormControl>
              <FormDescription className="font-default font-normal text-sm">Para comunicações oficiais.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="tip_details" rules={{
      required: "O objetivo profissional é obrigatório",
      minLength: {
        value: 20,
        message: "Por favor, descreva seu objetivo com mais detalhes."
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default font-medium">Objetivo Profissional <span className="text-destructive">*</span></FormLabel>
            <FormControl>
              <Textarea placeholder="Descreva brevemente seu momento de carreira e como a MASTERPRO+ pode potencializar seu legado..." className="min-h-[150px] resize-y bg-background border-border focus:border-primary" {...field} value={field.value || ''} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="location" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default font-medium">Cargo Atual (opcional)</FormLabel>
            <FormControl>
              <Input placeholder="Ex: Diretor Executivo / CEO" className="bg-background border-border focus:border-primary" {...field} value={field.value || ''} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="attachment" render={({
      field: {
        value,
        onChange,
        ...fieldProps
      }
    }) => <FormItem>
            <FormLabel className="font-default font-medium">Currículo ou LinkedIn (PDF)</FormLabel>
            <FormControl>
              <div className="relative">
                <Input type="file" className="pl-10 file:mr-4 file:py-1 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary/10 file:text-primary hover:file:bg-primary/20 cursor-pointer bg-background border-border" onChange={e => {
            const file = e.target.files?.[0];
            onChange(file);
          }} {...fieldProps} />
                <Paperclip className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
              </div>
            </FormControl>
            <FormDescription className="font-default font-normal text-sm">Anexe seu histórico profissional para uma análise prévia.</FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-sm text-destructive font-medium font-default">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 font-default font-bold uppercase tracking-wider" size="lg">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Processando...
          </> : <>
            <Send className="mr-2 size-4" />
            Solicitar Consultoria
          </>}
      </Button>
    </Form>;
}
export default function SubmitATip() {
  return <section data-section-id="247364" id="contato-e-conexão" className="bg-background text-foreground py-16 md:py-24 relative overflow-hidden border-y border-white/5">
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMiIgY3k9IjIiIHI9IjIiIGZpbGw9IiNmZmYiLz48L3N2Zz4=')] [mask-image:linear-gradient(to_bottom,white,transparent)]" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          
          <div className="lg:col-span-5 space-y-10">
            <div>
              <h2 className="font-serif font-bold text-4xl md:text-5xl text-foreground tracking-tight mb-4">
                Construa seu Legado
              </h2>
              <p className="font-default font-normal text-lg leading-relaxed text-muted-foreground">
                A MASTERPRO+ oferece orientação estratégica para líderes que buscam longevidade e impacto. Conecte-se com nossa rede exclusiva de networking corporativo.
              </p>
            </div>

            <div className="space-y-6">
              

              <div className="flex items-start space-x-4">
                <div className="mt-1 bg-primary/10 p-2.5 rounded-md shrink-0 border border-primary/20">
                  <Phone className="size-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-default font-semibold text-foreground text-lg">WhatsApp Business</h4>
                  <p className="text-muted-foreground mt-1 font-default">+ 55 11 966 187 866</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="mt-1 bg-primary/10 p-2.5 rounded-md shrink-0 border border-primary/20">
                  <Mail className="size-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-default font-semibold text-foreground text-lg">E-mail Direto</h4>
                  <Link to="mailto:contato@masterproplus.com.br" className="text-primary hover:text-primary/80 transition-colors mt-1 block font-default">
                    contato@masterproplus.com.br
                  </Link>
                </div>
              </div>
            </div>

            
          </div>

          <div className="lg:col-span-7">
            <Card className="border-white/10 shadow-4xl bg-card/40 backdrop-blur-xl rounded-xl overflow-hidden">
              <CardHeader className="pb-6 border-b border-white/5 bg-white/5">
                <CardTitle className="font-serif font-bold text-3xl text-foreground">Solicitar Consultoria</CardTitle>
                <CardDescription className="font-default font-normal text-base mt-2 text-muted-foreground">
                  Suas informações serão tratadas com o mais alto nível de sigilo executivo por nossos consultores seniores.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-8">
                <FormProvider formKey="contact_form_home" formId="contact_form_home" sectionName="Contato e Conexão" defaultValues={{
                anonymous: false,
                name: "",
                email: "",
                tip_details: "",
                location: "",
                attachment: undefined
              }} submitText="Solicitar Consultoria" successMessage="Sua solicitação foi recebida. Um de nossos consultores seniores entrará em contato em até 24 horas úteis.">
                  <TipForm />
                </FormProvider>
              </CardContent>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}